import React, { useEffect, useState } from "react";
import {
  AppShell,
  Group,
  ActionIcon,
  Tooltip,
  Button,
  Badge,
  Modal,
  Text,
  Stack,
  Switch,
  ThemeIcon,
  Divider,
  Box,
  SegmentedControl,
  NumberInput,
  UnstyledButton,
  Collapse,
} from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobal } from "./context/global-context";
import ClientApplicationToggle from "./components/client-application-toggle";
import CartIcon from "./components/cart-icon";
import { IconSettings, IconLock, IconToggleRight, IconBrowserCheck, IconExternalLink, IconRefresh, IconLogout, IconShieldCheck, IconFileCheck, IconChevronDown } from "@tabler/icons-react";
import UnderTheHoodDrawer, { UnderTheHoodButton } from "./components/under-the-hood";
import { signOut } from "./lib/authClient";
import { CONSENT_MODE_VX, CONSENT_MODE_SITE, consentMode } from "./utils/consent";
import { useMediaQuery } from "@mantine/hooks";
import "./styles/wine-theme.css";
import "./app-shell.css";
export default function MainAppShell({ content }) {
  const { globalState, wineStoreSettings, updateWineStoreSettings, updateSocialMediaFlow, updateFintechFlow, clearCart } = useGlobal();
  const [settingsOpened, setSettingsOpened] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const handleStartOver = () => {
    if (location.pathname === '/wine' || location.pathname === '/' || location.pathname === '/checkout' || location.pathname === '/checkout/return') {
      clearCart();
      window.location.href = '/wine';
    } else if (location.pathname === '/kids-games') {
      updateSocialMediaFlow({ step: "landing", username: "", email: "" });
      window.location.href = '/kids-games';
    } else if (location.pathname === '/register') {
      updateFintechFlow({ step: "landing", email: "", firstName: "", lastName: "", phone: "", ssn4: "" });
      window.location.href = '/register';
    }
  };
  const location = useLocation(); // Use useLocation
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const [clientApplication, setClientApplication] = useState(
    `${location.pathname}?${searchParams.toString()}`
  );
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    // Don't navigate if we're on special pages like checkout or configuration
    const currentPath = window.location.pathname;
    const specialPages = ['/checkout', '/checkout/return', '/configuration'];
    
    if (!specialPages.includes(currentPath)) {
      navigate(clientApplication);
    }
    console.log("client app is", clientApplication);
  }, [clientApplication, navigate]);


  const isWine = ['/wine', '/', '/checkout', '/checkout/return'].includes(location.pathname);
  const isDemoPage = isWine || location.pathname === '/kids-games' || location.pathname === '/register';
  const headerHeight = isWine ? (isMobile ? 60 : 72) : 100;
  const underTheHoodOpen = !!wineStoreSettings.underTheHoodOpen;
  const dataFieldsOpen = !!wineStoreSettings.dataFieldsOpen;
  // The wine store is an alcohol demo, so it keeps its own minimum age (21 by
  // default) instead of sharing the one used by the fintech and social demos.
  const ageKey = isWine ? "wineAge" : "age";

  return (
    <AppShell
      header={{ height: headerHeight }}
    >
      <AppShell.Header
        withBorder={isWine}
        className={isWine ? "vt-wine vt-shell-header" : undefined}
        style={isWine ? undefined : { paddingLeft: "10px", paddingRight: "40px" }}
      >
        <Group h="100%" px={isWine ? 0 : "md"} justify="space-between" wrap="nowrap" gap={isWine && isMobile ? 10 : "md"}>
          <ClientApplicationToggle />

          <Group wrap="nowrap" gap={isWine && isMobile ? 6 : 8} style={{ flexShrink: 0 }}>
            {isDemoPage && (
            <Group wrap="nowrap" gap={isWine && isMobile ? 6 : 8} style={{ flexShrink: 0 }}>
              {isWine && <CartIcon />}
              <Tooltip label="Demo Settings">
                <ActionIcon
                  variant="default"
                  size="lg"
                  className={isWine ? "vt-icon-btn" : undefined}
                  onClick={() => setSettingsOpened(true)}
                >
                  <IconSettings size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Start Over">
                <ActionIcon
                  variant="default"
                  size="lg"
                  className={isWine ? "vt-icon-btn" : undefined}
                  onClick={handleStartOver}
                >
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
              <UnderTheHoodButton
                open={underTheHoodOpen}
                compact={isMobile}
                onToggle={() => updateWineStoreSettings({ underTheHoodOpen: !underTheHoodOpen })}
              />
            </Group>
            )}
            <Tooltip label="Sign out">
              <ActionIcon
                variant="default"
                size="lg"
                aria-label="Sign out"
                className={isWine ? "vt-icon-btn" : undefined}
                onClick={signOut}
              >
                <IconLogout size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>

        </Group>
      </AppShell.Header>
      <AppShell.Main style={{ backgroundColor: isWine ? "#F4F5F5" : "#fafafa", padding: isWine ? 0 : undefined, paddingTop: isWine ? headerHeight : undefined }}>
        {content}
      </AppShell.Main>

      <UnderTheHoodDrawer
        opened={underTheHoodOpen}
        onClose={() => updateWineStoreSettings({ underTheHoodOpen: false })}
      />

      <Modal
        opened={settingsOpened}
        onClose={() => setSettingsOpened(false)}
        size="md"
        title={
          <Group gap="sm">
            <ThemeIcon size="md" variant="light" color="blue">
              <IconToggleRight size={16} />
            </ThemeIcon>
            <Text fw={600} size="lg">Implementation Scenarios</Text>
          </Group>
        }
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100dvh - 140px)",
          },
        }}
      >
        <div style={{ overflowY: "auto", padding: "var(--mantine-spacing-md)", flex: 1 }}>
          <Text size="xs" c="dimmed" mb="md">
            Configure which data elements are collected during the verification flow for demo purposes.
          </Text>

          <Group justify="space-between" px="sm" py={6} mb="md" style={{ borderRadius: 6, backgroundColor: "#f8f9fa" }}>
            <Text size="sm" fw={500}>Minimum Age</Text>
            <NumberInput
              size="md"
              w={120}
              min={1}
              max={120}
              allowDecimal={false}
              suffix="+"
              styles={{ input: { fontSize: "16px" } }}
              value={wineStoreSettings[ageKey] ?? 21}
              onChange={(value) => updateWineStoreSettings({ [ageKey]: value || 21 })}
            />
          </Group>

          <Stack gap="lg">
            {/* Data this website collects (collapsible, closed by default) */}
            <div>
              <UnstyledButton
                onClick={() => updateWineStoreSettings({ dataFieldsOpen: !dataFieldsOpen })}
                aria-expanded={dataFieldsOpen}
                aria-controls="vt-data-fields"
                style={{ width: "100%" }}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <div>
                    <Text size="sm" fw={600} c="dimmed">Data this website collects</Text>
                    <Text size="xs" c="dimmed" mt={2}>
                      What the site already asks the customer for; whatever is on is pre-filled into the VX journey (the target object) so the customer is not asked twice.
                    </Text>
                  </div>
                  <IconChevronDown
                    size={18}
                    style={{ flexShrink: 0, marginTop: 2, transition: "transform 200ms ease", transform: dataFieldsOpen ? "rotate(180deg)" : "none" }}
                  />
                </Group>
              </UnstyledButton>
              <Collapse in={dataFieldsOpen} id="vt-data-fields">
              <Stack gap="xs" mt="sm">
                <Group justify="space-between" px="sm" py={6} style={{ borderRadius: 6, backgroundColor: "#f8f9fa" }}>
                  <Text size="sm">Email</Text>
                  <Badge size="xs" color="gray" variant="light">Required</Badge>
                </Group>
                {[
                  { key: "firstName", label: "First Name" },
                  { key: "lastName", label: "Last Name" },
                  { key: "phone", label: "Phone" },
                  { key: "dob", label: "Date of Birth" },
                  { key: "ssn4", label: "SSN (Last 4)" },
                  { key: "address", label: "Address" },
                ].map((field) => (
                  <Group key={field.key} justify="space-between" px="sm" py={6} style={{ borderRadius: 6, backgroundColor: "#f8f9fa" }}>
                    <Text size="sm">{field.label}</Text>
                    <Switch
                      size="sm"
                      checked={wineStoreSettings.optionalFields[field.key] || false}
                      onChange={(e) => updateWineStoreSettings({
                        optionalFields: { ...wineStoreSettings.optionalFields, [field.key]: e.currentTarget.checked }
                      })}
                    />
                  </Group>
                ))}
              </Stack>
              </Collapse>
            </div>

            <Divider />

            {/* Integration Method */}
            <div>
              <Text size="sm" fw={600} c="dimmed" mb="sm">Integration Method</Text>
              <SegmentedControl
                fullWidth
                value={wineStoreSettings.integrationMethod || "iframe"}
                onChange={(value) => updateWineStoreSettings({ integrationMethod: value })}
                data={[
                  {
                    value: "iframe",
                    label: (
                      <Group gap={6} justify="center">
                        <IconBrowserCheck size={16} />
                        <span>iFrame</span>
                      </Group>
                    ),
                  },
                  {
                    value: "redirect",
                    label: (
                      <Group gap={6} justify="center">
                        <IconExternalLink size={16} />
                        <span>Redirect</span>
                      </Group>
                    ),
                  },
                ]}
              />
            </div>

            <Divider />

            {/* Notice and consent */}
            <div>
              <Text size="sm" fw={600} c="dimmed" mb="sm">Notice and consent</Text>
              <SegmentedControl
                fullWidth
                value={consentMode(wineStoreSettings)}
                onChange={(value) => updateWineStoreSettings({ noticeAndConsent: value })}
                data={[
                  {
                    value: CONSENT_MODE_VX,
                    label: (
                      <Group gap={6} justify="center">
                        <IconShieldCheck size={16} />
                        <span>Shown by VX</span>
                      </Group>
                    ),
                  },
                  {
                    value: CONSENT_MODE_SITE,
                    label: (
                      <Group gap={6} justify="center">
                        <IconFileCheck size={16} />
                        <span>Handled by this site</span>
                      </Group>
                    ),
                  },
                ]}
              />
              <Text size="xs" c="dimmed" mt="xs">
                {consentMode(wineStoreSettings) === CONSENT_MODE_SITE
                  ? "This site shows its own terms and privacy notice, collects consent, and attests to it in the create-journey request so VX skips its consent screen."
                  : "VX shows its own notice-and-consent screen at the start of verification."}
              </Text>
            </div>

            <Divider />

            {/* Test Mode */}
            <div>
              <Text size="sm" fw={600} c="dimmed" mb="sm">Test Mode</Text>
              <Group justify="space-between" px="sm" py={6} style={{ borderRadius: 6, backgroundColor: "#f8f9fa" }}>
                <Text size="sm">
                  {wineStoreSettings.testMode ? "Test Mode On" : "Test Mode Off"}
                </Text>
                <Switch
                  size="sm"
                  checked={wineStoreSettings.testMode ?? true}
                  onChange={(e) => updateWineStoreSettings({ testMode: e.currentTarget.checked })}
                />
              </Group>
            </div>
          </Stack>
        </div>

        <div style={{ padding: "var(--mantine-spacing-md)", borderTop: "1px solid #e9ecef", background: "white" }}>
          <Button fullWidth size="md" onClick={() => setSettingsOpened(false)}>
            Done
          </Button>
        </div>
      </Modal>

    </AppShell>
  );
}
